import type { BusinessEkinetAccount } from '@this/domain/business_ekinet_account';
import React, { useState } from 'react';

import { Input } from '@this/shared/ui/inputs/input';
import { FormControl, InputLabel } from '@this/shared/ui/inputs/form_control';
import { ModalFooter, ModalBody, Modal, ModalHeader } from '@this/components/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

function getStyles() {
  return {
    inputAreaFlex: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    } as React.CSSProperties,
    validationError: {
      color: '#d32f2f',
      margin: '6px 10px'
    } as React.CSSProperties
  };
}

interface Props {
  businessEkinetAccount: BusinessEkinetAccount | null | undefined;
  open: boolean;
  onAbort: () => void;
  onSubmit: (businessEkinetAccount: BusinessEkinetAccount) => void;
}

const BusinessEkinetAccountForm: React.FC<Props> = ({ open, ...props }) => {
  return (
    <Modal open={open} onClose={props.onAbort} size="medium">
      <ModalContent {...props} />
    </Modal>
  );
};

const ModalContent = ({ businessEkinetAccount, onSubmit, onAbort }: Omit<Props, 'open'>) => {
  const styles = getStyles();
  const [passwordType, setPasswordType] = useState<'password' | 'text'>('password');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validateBusinessEkinetId = () => {
    return businessEkinetAccount?.businessEkinetId?.length ? '' : 'ビジネスえきねっと会員IDを入力してください。';
  };

  const validatePassword = () => {
    return businessEkinetAccount?.password?.length ? '' : 'パスワードを入力してください。';
  };

  const handleSubmit = () => {
    const errors = [validateBusinessEkinetId(), validatePassword()].filter(Boolean);
    setValidationErrors(errors);
    if (errors.length === 0 && businessEkinetAccount) {
      onSubmit(businessEkinetAccount);
    }
  };

  return (
    <>
      <ModalHeader>
        {businessEkinetAccount?.id !== 0
          ? 'ビジネスえきねっとアカウントを編集'
          : 'ビジネスえきねっとアカウントを作成'}
      </ModalHeader>
      <ModalBody>
        <FormControl fullWidth required>
          <InputLabel htmlFor="business_ekinet_id">ビジネスえきねっと会員ID</InputLabel>
          <Input
            id="business_ekinet_id"
            type="text"
            placeholder="会員ID"
            value={businessEkinetAccount?.businessEkinetId || ''}
            onChange={e => businessEkinetAccount?.setBusinessEkinetId(e.target.value)}
          />
        </FormControl>
        <FormControl margin="normal" fullWidth required>
          <InputLabel htmlFor="password">パスワード</InputLabel>
          <div style={styles.inputAreaFlex}>
            <Input
              id="password"
              placeholder="パスワード"
              type={passwordType}
              value={businessEkinetAccount?.password || ''}
              onChange={e => businessEkinetAccount?.setPassword(e.target.value)}
              fullWidth
            />
            <div
              onClick={() => setPasswordType(prevType => (prevType === 'password' ? 'text' : 'password'))}
              style={{ cursor: 'pointer' }}
            >
              {passwordType === 'password' ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </div>
          </div>
        </FormControl>
        {validationErrors.length > 0 && (
          <div style={styles.validationError}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="sub" onClick={onAbort}>
          キャンセル
        </Button>
        <Button onClick={handleSubmit}>{businessEkinetAccount?.id !== 0 ? '更新' : '作成'}</Button>
      </ModalFooter>
    </>
  );
};

export default BusinessEkinetAccountForm;
